import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Package,
  Search,
  Loader2,
  Truck,
  ExternalLink,
  FileText,
  MapPin,
} from 'lucide-react';
import { fetchOrderByNumber } from '../services/orderService';
import { getInvoiceDownloadUrl, trackShipmentLive } from '../services/invoiceService';

const STATUS_STEPS = ['paid', 'shipped', 'delivered'];
const STATUS_LABELS = {
  pending: 'Payment Pending',
  paid: 'Order Confirmed',
  failed: 'Payment Failed',
  cancelled: 'Cancelled',
  shipped: 'Shipped',
  delivered: 'Delivered',
};

export default function TrackOrder() {
  const [orderNumber, setOrderNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [order, setOrder] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [error, setError] = useState('');
  const [invoiceError, setInvoiceError] = useState('');
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    setError('');
    setInvoiceError('');
    setOrder(null);
    setTracking(null);
    setSearched(true);

    if (!orderNumber.trim()) {
      setError('Enter your order ID');
      return;
    }
    if (!/^\d{10}$/.test(phone.replace(/\D/g, ''))) {
      setError('Enter the 10-digit phone number used at checkout');
      return;
    }

    setLoading(true);
    try {
      const result = await fetchOrderByNumber(orderNumber, phone);
      if (!result) {
        setError('No order found. Check your order ID and phone number.');
        return;
      }
      setOrder(result);

      if (['paid', 'shipped', 'delivered'].includes(result.status)) {
        try {
          const live = await trackShipmentLive(result.order_number, phone);
          if (live?.order) {
            setOrder((prev) => ({ ...prev, ...live.order }));
          }
          setTracking(live?.tracking || null);
        } catch (trackErr) {
          console.warn('Live tracking unavailable:', trackErr);
        }
      }
    } catch (err) {
      console.error(err);
      setError('Could not fetch order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadInvoice = async () => {
    if (!order) return;
    setInvoiceError('');
    setInvoiceLoading(true);
    try {
      const { signedUrl } = await getInvoiceDownloadUrl({
        orderNumber: order.order_number,
        phone,
      });
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error(err);
      setInvoiceError(err.message || 'Could not download invoice.');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const displayStatus = order?.status;
  const currentStep = STATUS_STEPS.indexOf(displayStatus);
  const canTrackShipment = order?.tracking_url || order?.awb_code;
  const trackingActivities = tracking?.activities || [];

  return (
    <main className="min-h-screen">
      <section className="bg-[#fdf8f6] py-16 sm:py-20 text-center px-6">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white border border-gray-100 mb-4">
          <Package size={22} className="text-charcoal" />
        </div>
        <p className="text-xs tracking-[0.3em] uppercase text-gray-400 mb-2">Order Status</p>
        <h1 className="font-display text-4xl sm:text-5xl font-light">Track Order</h1>
        <p className="text-sm text-gray-500 font-light mt-3 max-w-md mx-auto">
          Enter your order ID and registered phone number to check delivery status.
        </p>
      </section>

      <div className="max-w-xl mx-auto px-6 py-12">
        <form onSubmit={handleSearch} className="space-y-6">
          <div>
            <label className="block text-xs tracking-[0.15em] uppercase text-gray-400 mb-1.5">
              Order ID
            </label>
            <input
              type="text"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value.toUpperCase())}
              placeholder="e.g. MM20250612A3F9"
              className="w-full border-b border-gray-200 focus:border-black outline-none py-2.5 text-sm bg-transparent font-light uppercase tracking-wider"
            />
          </div>
          <div>
            <label className="block text-xs tracking-[0.15em] uppercase text-gray-400 mb-1.5">
              Phone Number
            </label>
            <div className="flex items-center border-b border-gray-200 focus-within:border-black transition-colors">
              <span className="text-sm text-gray-500 pr-2 py-2.5 font-light">+91</span>
              <input
                type="tel"
                inputMode="numeric"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                placeholder="9876543210"
                className="flex-1 outline-none py-2.5 text-sm bg-transparent font-light"
              />
            </div>
          </div>

          {error && <p className="text-xs text-red-500">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-black text-white py-3.5 text-xs tracking-[0.2em] uppercase font-medium hover:bg-gray-900 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={14} />}
            Track Order
          </button>
        </form>

        {order && (
          <div className="mt-12 border border-gray-100 p-6 sm:p-8">
            <div className="flex justify-between items-start mb-6 gap-4">
              <div>
                <p className="text-xs tracking-[0.15em] uppercase text-gray-400 mb-1">Order ID</p>
                <p className="font-medium text-lg">{order.order_number}</p>
              </div>
              <span className="text-xs tracking-widest uppercase px-3 py-1 bg-gray-50 border border-gray-100 shrink-0">
                {STATUS_LABELS[displayStatus] || displayStatus}
              </span>
            </div>

            {['paid', 'shipped', 'delivered'].includes(displayStatus) && (
              <div className="flex items-center justify-between mb-8 px-2">
                {STATUS_STEPS.map((step, i) => (
                  <React.Fragment key={step}>
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-3 h-3 rounded-full ${
                          currentStep >= i ? 'bg-black' : 'bg-gray-200'
                        }`}
                      />
                      <p className="text-[10px] tracking-wider uppercase mt-2 text-gray-500">
                        {STATUS_LABELS[step]}
                      </p>
                    </div>
                    {i < STATUS_STEPS.length - 1 && (
                      <div
                        className={`flex-1 h-px mx-2 ${
                          currentStep > i ? 'bg-black' : 'bg-gray-200'
                        }`}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}

            {(canTrackShipment || order.shipment_status) && (
              <div className="mb-6 p-4 bg-[#fdf8f6] border border-gray-100">
                <div className="flex items-center gap-2 mb-3">
                  <Truck size={16} className="text-gray-500" />
                  <p className="text-xs tracking-[0.15em] uppercase text-gray-400">
                    Shipment Details
                  </p>
                </div>
                {order.courier_name && (
                  <p className="text-sm mb-1">
                    <span className="text-gray-500">Courier:</span> {order.courier_name}
                  </p>
                )}
                {order.awb_code && (
                  <p className="text-sm mb-1">
                    <span className="text-gray-500">AWB:</span>{' '}
                    <span className="font-mono tracking-wide">{order.awb_code}</span>
                  </p>
                )}
                {order.shipment_status && (
                  <p className="text-sm mb-3">
                    <span className="text-gray-500">Status:</span> {order.shipment_status}
                  </p>
                )}
                {order.tracking_url && (
                  <a
                    href={order.tracking_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs tracking-widest uppercase border-b border-black pb-0.5 hover:text-gray-600"
                  >
                    Track on courier site
                    <ExternalLink size={12} />
                  </a>
                )}
              </div>
            )}

            {trackingActivities.length > 0 && (
              <div className="mb-6 border-t border-gray-100 pt-4">
                <p className="text-xs tracking-[0.15em] uppercase text-gray-400 mb-3">
                  Tracking Timeline
                </p>
                <div className="space-y-3">
                  {trackingActivities.slice(0, 6).map((activity, index) => (
                    <div key={`${activity.date}-${index}`} className="flex gap-3 text-sm">
                      <MapPin size={14} className="text-gray-400 mt-0.5 shrink-0" />
                      <div>
                        <p>{activity.activity || activity.status}</p>
                        {(activity.location || activity.date) && (
                          <p className="text-xs text-gray-400 mt-0.5">
                            {[activity.location, activity.date].filter(Boolean).join(' · ')}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2 text-sm border-t border-gray-100 pt-4">
              <div className="flex justify-between">
                <span className="text-gray-500">Order Date</span>
                <span>
                  {new Date(order.created_at).toLocaleDateString('en-IN', { dateStyle: 'medium' })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total</span>
                <span>₹{order.total_amount.toLocaleString()}</span>
              </div>
              {order.coupon_code && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Coupon</span>
                  <span>{order.coupon_code}</span>
                </div>
              )}
              {order.shipped_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Shipped</span>
                  <span>
                    {new Date(order.shipped_at).toLocaleDateString('en-IN', { dateStyle: 'medium' })}
                  </span>
                </div>
              )}
              {order.delivered_at && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Delivered</span>
                  <span>
                    {new Date(order.delivered_at).toLocaleDateString('en-IN', {
                      dateStyle: 'medium',
                    })}
                  </span>
                </div>
              )}
            </div>

            {order.items?.length > 0 && (
              <div className="mt-6 border-t border-gray-100 pt-4">
                <p className="text-xs tracking-[0.15em] uppercase text-gray-400 mb-3">Items</p>
                {order.items.map((item) => (
                  <div key={item.id} className="flex gap-3 mb-3">
                    {item.product_image && (
                      <img
                        src={item.product_image}
                        alt={item.product_name}
                        className="w-12 h-12 object-cover rounded"
                      />
                    )}
                    <div className="flex-1 text-sm">
                      <p>{item.product_name}</p>
                      <p className="text-xs text-gray-400">Qty {item.quantity}</p>
                    </div>
                    <p className="text-sm">₹{item.line_total.toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}

            {order.shipping_address && (
              <div className="mt-6 text-sm border-t border-gray-100 pt-4">
                <p className="text-xs tracking-[0.15em] uppercase text-gray-400 mb-2">Shipping to</p>
                <p className="font-medium">{order.shipping_name}</p>
                <p className="text-gray-500 font-light mt-1">
                  {order.shipping_address}, {order.shipping_city} — {order.shipping_pincode}
                </p>
              </div>
            )}

            {order.status === 'paid' ||
            order.status === 'shipped' ||
            order.status === 'delivered' ? (
              <div className="mt-6 border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={handleDownloadInvoice}
                  disabled={invoiceLoading}
                  className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-medium border border-black px-4 py-2.5 hover:bg-black hover:text-white transition-colors disabled:opacity-50"
                >
                  {invoiceLoading ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <FileText size={14} />
                  )}
                  Download Invoice
                </button>
                {invoiceError && <p className="text-xs text-red-500 mt-2">{invoiceError}</p>}
                {order.invoice_number && (
                  <p className="text-xs text-gray-400 mt-2">Invoice: {order.invoice_number}</p>
                )}
              </div>
            ) : null}
          </div>
        )}

        {searched && !order && !loading && !error && null}

        <p className="text-xs text-gray-400 text-center mt-10">
          Need help?{' '}
          <Link to="/contact" className="underline hover:text-black">
            Contact us
          </Link>
        </p>
      </div>
    </main>
  );
}
